/**
 * Виджеты категории "Время": обратный отсчёт, часы, дата, календарь.
 *
 * Каждый объект — источник данных для лендинга /widgets/:slug.
 * Тексты уникальны для каждого виджета: общие блоки-болванки не используем,
 * иначе страницы попадают под Scaled Content Abuse.
 *
 * Факты — под версию расширения 4.0.0, из
 * data/screenshots/report-4-time-productivity.md (у каждого там ссылка на
 * код) и report-4-new-widgets.md. Блоки screenshot и section.image пока
 * описывают кадры 3.x: их заменят вместе с новыми скриншотами.
 *
 * @see docs/seo/SEO-STRATEGY.md
 * @see docs/SEO_WRITER.md
 */

export default [
	{
		slug: 'countdown',
		name: 'Countdown',
		category: 'Time',
		plan: 'free',
		model: 'PlainCountDownModel',
		title: 'Countdown Timer for Chrome New Tab',
		metaDescription:
			'A countdown timer on every Chrome new tab. Count down to an exam, a launch or one of 23 holidays, in six designs, with a burst of confetti when it hits zero.',
		h1: 'Countdown timer for your Chrome new tab',
		lead:
			'This countdown timer sits on your Chrome new tab, so the exam, the launch or the flight stares back at you every time you open one. Pick a date or a holiday, pick a design, and get back to work. Or don’t, and watch the seconds go.',
		screenshot: {
			file: 'countdown-hero.webp',
			alt:
				'Four countdown widgets over an autumn forest: an orange Halloween poster, vacation cards, a bold launch timer and exam rings',
			caption:
				'Halloween, a vacation, a launch and exams, each counting down in its own style',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Six designs, and the one you start with',
				p: [
					'Add a countdown from the menu and it opens in Cards, one tile per unit, with digits that roll over as they change. On a plain light background the tiles come out light with dark text, so nothing disappears into the page.',
					'The Design button holds the other five. Ring draws a ring for each unit, and the biggest one shows how much of the period is left. Minimal is a single line like “12 days 04 h 37 min” with a thin progress bar. Poster blows the main number up, writes “to go” under it and shrinks everything else into chips. Bold starts with a text shadow already on.',
					'Classic is the look the widget had before 4.0. Countdowns you already had, and the ones inside gallery themes, stay in it until you pick something else.',
				],
			},
			{
				h2: 'Exam countdown or holiday countdown',
				p: [
					'For an exam countdown, a deadline or a trip, choose Custom date and set the day and time. The picker won’t offer dates that have already passed.',
					'Or switch to Holiday. There are 23 of them, sorted by whichever comes next, each with its date and “in N days”: Easter and Orthodox Easter, Ramadan, Chinese New Year, Diwali, Halloween, Thanksgiving, Christmas, New Year’s Eve and more. The date is worked out for you every year, and the holiday’s name becomes the title unless you type one.',
					'On the day itself it doesn’t leap straight to next year. Until midnight you get the holiday’s name and “Today!”, which is rather the point of counting to Christmas.',
				],
				image: {
					file: 'countdown-holiday.webp',
					alt:
						'Countdown settings with a grid of holidays, each with an emoji and how many days are left',
					caption:
						'Pick a holiday once and it rolls over to next year by itself',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Repeats and weekdays',
				p: [
					'A one-off date runs out. Rent day and the monthly report don’t. Repeat every rolls the countdown on to the next round, and Monthly or Yearly costs nothing. Daily, Weekly and Every 2 weeks are the PRO ones.',
					'Weekdays only, under Counting, throws out Saturdays and Sundays (just those two, public holidays still count) and treats a week as five working days. A deadline three weeks away suddenly has fifteen days in it. Grim, and usually the number you needed.',
				],
			},
			{
				h2: 'What’s on it, and what happens at zero',
				p: [
					'Units are switches. The new designs run from years down to seconds, and Classic adds milliseconds for the truly dramatic. A fresh countdown starts with weeks off, and the last unit standing cannot be switched off, so the widget never goes blank.',
					'Title puts up to 40 characters above the numbers. Show target date adds a line like “Until Oct 26, 2:23 PM”. Accent color tints the rings, the bar and the poster’s gradient, and Layout turns the row into a column.',
					'At zero the new designs read “Countdown is over!” and, if the tab happens to be open, throw confetti for a couple of seconds. There’s no sound and no notification — it’s a countdown on a page, not an alarm clock.',
				],
				image: {
					file: 'countdown-finish.webp',
					alt:
						'A purple poster reading Countdown is over with confetti flying, under a clock showing 17:00',
					caption: 'Zero hour, confetti included',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'A countdown timer for the desktop, browser style',
				p: [
					'Anyone after a countdown timer for desktop, or a desktop background countdown timer, wants the number in view without opening anything. This is the browser version. The numbers sit on top of your new tab wallpaper, and you see them each time you open a tab rather than when you minimise every window.',
					'It is not a kitchen timer, though. It counts to a date and time. For a short focus session the Pomodoro widget is the one you want.',
				],
			},
		],
		settings: [
			{
				name: 'Count down to',
				detail:
					'Custom date for any future day and time, or Holiday for one of 23, always the next one.',
			},
			{
				name: 'Repeat every',
				detail:
					'Monthly and Yearly are free. Daily, Weekly and Every 2 weeks need PRO. Only this countdown has repeats.',
			},
			{
				name: 'Units',
				detail:
					'Years to seconds, or to milliseconds in Classic. Weeks start switched off.',
			},
			{
				name: 'Weekdays only',
				detail:
					'Skips Saturdays and Sundays. It does not know about public holidays.',
			},
			{
				name: 'Design',
				detail:
					'Classic, Cards, Bold, Ring, Minimal or Poster, from the toolbox or the top of the settings window.',
			},
			{
				name: 'Title and target date',
				detail:
					'New designs only. A label of up to 40 characters, and an “Until …” line above the digits.',
			},
			{
				name: 'Accent color',
				detail:
					'Eight colours plus Default, for the rings, the progress bar and the poster.',
			},
			{
				name: 'Layout',
				detail:
					'Horizontal or Vertical. There’s a toolbox button for it too.',
			},
			{
				name: 'Size and type',
				detail:
					'There’s no font-size box. Stretch the frame and the countdown grows with it. Any of the 22 fonts works; a text shadow exists only in Classic and Bold.',
			},
		],
		useCases: [
			'Exam countdown to finals, the bar exam or a driving test.',
			'A launch date on a team page everyone installs as a theme.',
			'Christmas, Diwali or Ramadan, picked from the list once and left alone.',
			'Rent day or payday, repeating monthly on the free plan.',
			'A hand-in date counted in weekdays only.',
			'A trip, with the city as the title and a photo of it as the wallpaper.',
		],
		faq: [
			{
				q: 'Is there a free countdown widget for Chrome?',
				a:
					'Yes, New Tab Studio’s countdown widget is free and shows a countdown to any date on every Chrome new tab. All six designs, 23 holidays and monthly or yearly repeats cost nothing. PRO adds daily, weekly and fortnightly repeats and lifts the cap of three widgets you place by hand, a cap that skips countdowns shipped inside a theme you installed.',
			},
			{
				q: 'How can I keep a countdown to my exam on my screen?',
				a:
					'Put an exam countdown on your Chrome new tab page, so every tab you open shows how long you’ve got. Choose Custom date, set the day and hour of the exam and add a title such as Finals, up to 40 characters. Weekdays only drops Saturdays and Sundays if school days are the number you care about.',
			},
			{
				q: 'What are fun ways to count down to Christmas?',
				a:
					'Fun ways to count down to Christmas include an Advent calendar, a paper chain that loses one link a night, a festive film every evening in December and a countdown on your browser’s new tab. For that last one, pick Christmas from the countdown’s 23 holidays and it moves on to next year by itself. On the day it says “Today!” until midnight.',
			},
			{
				q: 'Will the countdown beep or send a notification at zero?',
				a:
					'No, the countdown widget makes no sound and sends no notification when it hits zero. The new designs swap the digits for “Countdown is over!” and throw confetti for a couple of seconds if you’re looking at the tab at that moment, unless your system is set to reduce motion. Need an alarm as well? Set one on your phone.',
			},
			{
				q: 'Does the countdown keep counting when Chrome is closed?',
				a:
					'Yes, the countdown is right whenever you come back, because it works out the time left from the target date each time the tab is on screen. Close Chrome for a week, open a new tab, and the number is correct straight away. In a hidden tab it doesn’t tick at all and recalculates the moment you switch back.',
			},
		],
		related: ['day-countdown', 'count-up', 'habit-tracker', 'text'],
		keywords: [
			'countdown timer chrome extension',
			'chrome countdown timer',
			'countdown timer for desktop',
			'desktop background countdown timer',
			'exam countdown',
			'new tab countdown',
		],
	},

	{
		slug: 'count-up',
		name: 'Count Up',
		category: 'Time',
		plan: 'free',
		model: 'PlainCountUpModel',
		title: 'Count Up Timer for Chrome New Tab',
		metaDescription:
			'Days since you quit, started, launched or met someone. A count up timer for the Chrome new tab that only ever goes up, in six designs, poster included.',
		h1: 'Count up timer: days since, not days until',
		lead:
			'A count up timer runs the other way. Give it a date in the past and it shows how long it’s been: 211 days sober, six weeks at the new job, two years together. The number only climbs, and it’s there every time you open a tab.',
		screenshot: {
			file: 'count-up-hero.webp',
			alt:
				'A coral poster reading Together, 2 years and counting, next to cards counting 282 days since January 5',
			caption:
				'Counting up: two years together and 282 days since a January start',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A number you don’t want to reset',
				p: [
					'A countdown is pressure. A count up is what you’ve already banked, and for a streak that feels completely different. Day 1 is a win. Day 47 is something you’d rather not throw away over one cigarette.',
					'Teams run it as days since the last incident, on a shared theme everybody installs, and it quickly becomes the most closely guarded number in the office, because nobody wants to be the one who sends it back to zero. Nobody.',
				],
			},
			{
				h2: 'Poster, and the “and counting” line',
				p: [
					'The designs are the countdown’s six: Classic, Cards, Bold, Ring, Minimal, Poster. New count ups start in Cards, and anything set up before 4.0 keeps the Classic look.',
					'Poster is the one to try first. The biggest unit you’ve switched on turns into one huge number with “years and counting” or “days and counting” under it, and the smaller units sit next to it as chips. Add a title like Together or Smoke-free and it reads like a small banner — or a quiet boast.',
					'Without a title, Show target date prints “Since Sep 26, 2:23 PM”, handy when the date itself is the milestone.',
				],
			},
			{
				h2: 'What it leaves out',
				p: [
					'It shares its settings window with the countdown, but only part of it. No holidays, no repeats, no weekdays-only mode, and the start date picker offers only the past.',
					'It never ends, so there is no finish screen, no confetti and no progress bar (there’s nothing to measure progress against). It keeps adding days until you move the start date.',
				],
			},
		],
		settings: [
			{
				name: 'Count up from',
				detail:
					'A start date and time in the past. Future dates are greyed out.',
			},
			{
				name: 'Units',
				detail:
					'Years down to seconds in the new designs, milliseconds as well in Classic. Weeks begin switched off.',
			},
			{
				name: 'Title',
				detail:
					'Up to 40 characters above the number, in the new designs. The placeholder suggests Smoke-free, which tells you who it was built for.',
			},
			{
				name: 'Accent color',
				detail:
					'Eight colours or Default. On Poster it paints the gradient, which is where you notice it most.',
			},
			{
				name: 'Layout',
				detail:
					'A row or a column, switched in settings or from the toolbox.',
			},
			{
				name: 'Look',
				detail:
					'Font and colours come from the toolbox, size from stretching the frame. A text shadow is only offered in Classic and Bold.',
			},
		],
		useCases: [
			'Sober, smoke-free or sugar-free streaks.',
			'Days since you started a language, a course or an instrument.',
			'Time at a new job, in a new city, or together.',
			'Days since launch or since the last incident, on a team theme.',
			'The age of a project, a puppy or a sourdough starter.',
		],
		faq: [
			{
				q: 'How do I count the days since something happened?',
				a:
					'To count the days since something happened, use a count-up timer, which starts from a date in the past and keeps adding days. A date calculator such as timeanddate.com gives you the number once, while New Tab Studio’s Count Up keeps it on every new tab. Set the start date, add a title like Smoke-free, and choose which units to show, from years down to seconds.',
			},
			{
				q: 'Is there a free sobriety or streak counter for Chrome?',
				a:
					'Yes, the count-up widget works as a free sobriety or streak counter on the Chrome new tab, and it has no PRO settings at all. Sober days, smoke-free days or a no-sugar run cost nothing to track. The free plan only limits how many widgets you place yourself, three, and a counter inside an installed theme isn’t one of them.',
			},
			{
				q: 'How is a count up different from a countdown?',
				a:
					'A count up measures time since a past date and never ends, while a countdown aims at a future date and finishes at zero. So the count up has no holidays, no repeats, no weekdays-only mode, no confetti and no progress bar. The six designs are shared, and Poster writes “days and counting” under its big number.',
			},
			{
				q: 'How do I reset a streak?',
				a:
					'To reset a count-up streak, open the widget’s settings and move the start date to today, and the number starts climbing again from there. Want to keep the old record in view? Add a second count up first and call it Best run, bearing in mind that it takes another widget slot.',
			},
			{
				q: 'Will my count up move to a new computer?',
				a:
					'Yes, a count up moves with your page. Export the page as a theme file or publish it, then install it on the other machine. The start date travels inside the widget, so the number picks up exactly where it was.',
			},
		],
		related: ['countdown', 'habit-tracker', 'text', 'quotes'],
		keywords: [
			'count up timer new tab',
			'days since counter chrome',
			'sobriety counter extension',
			'streak counter new tab',
		],
	},

	{
		slug: 'clock',
		name: 'Digital Clock',
		category: 'Time',
		plan: 'free',
		model: 'ClockModel',
		title: 'Digital Clock for Chrome New Tab',
		metaDescription:
			'A clock for Chrome’s new tab in 12 formats, from 24-hour to 18:35 Uhr. Pick a city and it becomes a world clock that handles daylight saving on its own.',
		h1: 'Digital clock for Chrome, with world time',
		lead:
			'A clock for Chrome that isn’t eleven pixels tall in the corner of your screen. It lives on the new tab, as big as you like, in any of 22 fonts, and if half the team is in another time zone it’ll show their time too.',
		screenshot: {
			file: 'clock-hero.webp',
			alt:
				'Three text clocks in a row reading 09:41, 14:41 and 22:41, labelled New York, London and Tokyo, over a night city',
			caption:
				'Three cities on one tab, with daylight saving time handled for you',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Twelve formats, previewed live',
				p: [
					'Open Clock settings and every entry in Clock format is shown on the current time, so you choose by looking. 24-hour is the default. Next come 24-hour with seconds, am/pm, 12-hour and 12-hour with seconds, then the regional styles: Nordic 18.35, German 18:35 Uhr, French 18 h 35, Japanese 18時35分, Chinese and Korean.',
					'As in my region follows your browser’s language rather than the extension’s. British English gets 24 hours, American English gets 12. Small thing, saves an argument.',
					'One quirk. The am/pm format keeps the leading zero, 02:05 pm. For 2:05 PM, pick 12-hour.',
				],
				image: {
					file: 'clock-formats.webp',
					alt:
						'Clock settings with the format list open, showing 18:35 written twelve ways, from 24-hour to Japanese and Korean',
					caption:
						'18:35, 6:35 PM, 18 h 35 or 18時35分: pick how your clock writes it',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'A world clock in Google Chrome',
				p: [
					'The Timezone list has three groups. From my computer comes first, then 99 cities that switch to daylight saving by themselves, then 37 fixed offsets from UTC. Choose London, Edinburgh — the city, not an offset — and the clock follows British Summer Time. It keeps the right time even in the hour your computer changes its clocks.',
					'For a world clock, line up three of them and put a text widget under each with the city. Time zones are free on this clock. Just remember that three clocks plus three labels make six widgets, and the free plan lets you add three yourself (widgets from an installed theme don’t count).',
				],
			},
			{
				h2: 'Big is the whole idea',
				p: [
					'There is no font-size box. Drag the corner and the digits grow with the frame, from a corner readout to half the screen. Change the format and the frame refits itself, so adding seconds doesn’t push into the widget next door.',
					'At large sizes the font matters more than the colour. Rubik Mono and Press Start keep their shape when they’re huge; the handwriting faces look better smaller, beside a photo.',
					'What it does not have is designs or a vertical layout. It’s the plain text clock, and the menu calls it Text clock. For flip cards, neon or Nixie tubes there’s the animated clock, and for hands, the analog one.',
				],
			},
		],
		settings: [
			{
				name: 'Clock format',
				detail:
					'12 options, each shown on the current time. 24-hour unless you change it.',
			},
			{
				name: 'Timezone',
				detail:
					'Your computer’s time, one of 99 cities with daylight saving, or one of 37 fixed UTC offsets. Free on this clock.',
			},
			{
				name: 'Font',
				detail:
					'Any of the 22 in the toolbox. Monospace and display faces hold up best at poster size.',
			},
			{
				name: 'Colour, fill and shadow',
				detail:
					'Digit colour, a fill behind them and a text shadow for busy photos. Corner radius sits in the Border panel.',
			},
			{
				name: 'Size',
				detail:
					'Drag the corner. The frame re-measures itself after you change the format or time zone.',
			},
			{
				name: 'Opening settings',
				detail:
					'Double-click the clock, or use the gear in the toolbox.',
			},
		],
		useCases: [
			'One huge clock in the middle of a minimal page.',
			'A row of clocks for a team split between New York, London and Tokyo.',
			'A small corner clock on a page that’s mostly links.',
			'A desk clock on a second monitor parked on the new tab.',
			'German or Nordic time formats for people who never got on with am/pm.',
		],
		faq: [
			{
				q: 'How can I see clocks for several cities in Chrome?',
				a:
					'For a quick check, search Google for “time in Tokyo”, and Windows can add two extra clocks to its taskbar clock under Date & time settings. To keep several cities on the Chrome new tab, add New Tab Studio’s text clock once per city and choose each from 99 cities in the Timezone list. Three clocks fit the free plan, while text labels under them would need PRO.',
			},
			{
				q: 'Does the clock handle daylight saving time?',
				a:
					'Yes, the digital clock switches to daylight saving time by itself whenever you pick a city, and all 99 cities in the list do. Only the 37 fixed UTC offsets at the bottom stay put, and older themes built with offsets keep them. It even shows the right time in the hour your computer changes its own clock.',
			},
			{
				q: 'How do I put a clock on the Chrome new tab page?',
				a:
					'Chrome’s standard new tab page has no clock option, so a clock there comes from an extension. In New Tab Studio, Text clock lives under Time in the widget menu, reached from the button in the bottom-left corner. Drag its corner until the digits are as big as you want, and double-click it for formats and time zones.',
			},
			{
				q: 'How do I get 2:05 PM instead of 14:05?',
				a:
					'Pick 12-hour in Clock format and the clock writes 2:05 PM, or 12-hour with seconds if you want those too. The am/pm option keeps the leading zero, 02:05 pm. As in my region decides by your browser’s language, so British English gets 24 hours and American English gets 12.',
			},
			{
				q: 'Do time zones cost extra on the digital clock?',
				a:
					'No, the time zone on the text clock is free, and so are all 12 formats. The analog clock is the only one where another city needs PRO. What the free plan does limit is how many widgets you add by hand, and that number is three.',
			},
		],
		related: ['date', 'analog-clock', 'flip-clock', 'weather'],
		keywords: [
			'clock for chrome',
			'clock extension for chrome',
			'world clock google chrome',
			'digital clock new tab',
			'chrome new tab clock',
		],
	},

	{
		slug: 'analog-clock',
		name: 'Analog Clock',
		category: 'Time',
		plan: 'mixed',
		model: 'AnalogClockModel',
		title: 'Analog Clock Widget for Chrome New Tab',
		metaDescription:
			'An analog clock widget with seven faces, from a Swiss railway clock to a diver’s watch. Smooth or ticking second hand, and a city time zone on the PRO plan.',
		h1: 'Analog clock widget with seven faces',
		lead:
			'This analog clock widget puts a proper face with hands on your new tab, and version 4.0 gave it six new ones. Digits tell you the time. A face shows how much of the hour has gone, which helps if you think in blocks rather than timestamps.',
		screenshot: {
			file: 'analog-clock-hero.webp',
			alt:
				'Seven analog clock designs in a row over an evening sea, all showing ten past ten',
			caption: 'Seven faces, from railway station to diver’s watch',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Seven faces, one of them a station clock',
				p: [
					'Classic is the old face, and every analog clock you already had stays on it. New ones from the menu arrive in Glass, a bit under three quarters of full size. The other five are Railway, Minimal, Luxury, Soft and Diver, and Luxury and Diver also get a little date window.',
					'Railway is the one to watch. Like a Swiss station clock, the red second hand sweeps round in about 58 seconds, stops at 12 and waits for the minute hand to jump. Luxury’s small seconds hand moves in eight tiny steps a second, the way a mechanical watch does.',
				],
			},
			{
				h2: 'Smooth, ticking, or no second hand',
				p: [
					'Second hand motion is Smooth out of the box, one continuous sweep. Ticking jumps once a second. And if any movement at all distracts you, switch Second hand off. Classic is the one face without the motion setting.',
					'The hands are turned by CSS rather than redrawn frame by frame, and every 30 seconds the clock checks itself against the real time. Wake the laptop from sleep and any drift is gone by the next check.',
					'Set your system to reduce motion and the second hand steps once a second instead of sweeping.',
				],
				image: {
					file: 'analog-clock-settings.webp',
					alt:
						'Analog clock settings with seven design previews, second hand options, a date window switch and a timezone picker',
					caption: 'Smooth or ticking, date window on or off',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Time zones are PRO here',
				p: [
					'With PRO, each face can show a different city. The list is shared with the other clocks — 99 cities that handle daylight saving plus fixed UTC offsets — so three faces labelled London, Berlin and Singapore cover the team in one look.',
					'Without PRO the time zone field opens an upgrade prompt and the face shows your computer’s time. If digits are enough, the text clock and the animated clock do other cities for free.',
				],
			},
			{
				h2: 'Size, sharpness and styling',
				p: [
					'The clock is square with the aspect locked, on a 200×200 base. The six new faces are SVG, Classic is built from CSS blocks, and after a resize the hands are redrawn at the new scale, so a big face stays crisp.',
					'Styling is short. The new faces take one colour for the hands and marks and one for the dial. No border or rounding on those, and no shadow on any of the seven.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Railway, Minimal, Glass, Luxury, Soft, Diver. Old clocks stay Classic, new ones open in Glass.',
			},
			{
				name: 'Second hand',
				detail: 'On by default. Turn it off for a calmer face.',
			},
			{
				name: 'Second hand motion',
				detail: 'Smooth or Ticking. Classic does not have it.',
			},
			{
				name: 'Date window',
				detail:
					'The day of the month in a small window. Luxury and Diver only, on from the start.',
			},
			{
				name: 'Timezone',
				detail:
					'PRO. A city with daylight saving or a fixed UTC offset. Without PRO the face shows your computer’s time.',
			},
			{
				name: 'Colours',
				detail:
					'Hands and marks, plus the dial. That’s the full palette on the new faces.',
			},
			{
				name: 'Size',
				detail: 'Aspect-locked resize from a 200×200 base.',
			},
		],
		useCases: [
			'A row of faces for a team in three time zones (PRO).',
			'One large Railway clock in the middle of a quiet photo theme.',
			'Luxury with its date window instead of a separate date widget.',
			'Next to a digital clock, when you want the angle and the exact minute.',
		],
		faq: [
			{
				q: 'Is there a free analog clock for the Chrome new tab?',
				a:
					'Yes, New Tab Studio’s analog clock is free, with all seven faces, a smooth or ticking second hand and a date window on Luxury and Diver. Showing another city’s time is the one PRO feature here. Without PRO the face follows your computer’s clock, and the text and animated clocks show other cities at no cost.',
			},
			{
				q: 'Can I show analog clocks for several cities?',
				a:
					'Yes, with PRO each analog face can show its own city, picked from 99 cities that handle daylight saving or from fixed UTC offsets. Three faces set to London, Berlin and Singapore cover a spread-out team at a glance. Without PRO the time zone field opens an upgrade prompt instead of the list.',
			},
			{
				q:
					'Does an analog clock set to a city change for daylight saving time?',
				a:
					'Yes, an analog clock set to a city moves to summer time by itself, so London, Edinburgh switches to British Summer Time without your help. Only the fixed UTC offsets stay put, and older themes were built with those. Every 30 seconds the face also checks itself against the real time, which clears any drift after sleep.',
			},
			{
				q: 'Can the second hand tick instead of sweep?',
				a:
					'Yes, the analog clock’s Second hand motion setting offers Smooth, the default, or Ticking, which jumps once a second. Classic is the one face without that choice, and you can hide the second hand altogether. Railway copies a Swiss station clock, sweeping round in about 58 seconds and pausing at 12 for the minute hand.',
			},
		],
		related: ['clock', 'flip-clock', 'date', 'workspaces'],
		keywords: [
			'analog clock widget',
			'analog clock chrome extension',
			'analog clock new tab',
			'railway clock new tab',
			'world clock new tab',
		],
	},

	{
		slug: 'flip-clock',
		name: 'Animated Clock',
		category: 'Time',
		plan: 'free',
		model: 'FlipClockModel',
		title: 'Animated Flip Clock for Chrome New Tab',
		metaDescription:
			'A flip clock for the Chrome new tab, plus eight more animated designs, from neon to Nixie tubes. Double-click to switch between them. Time zones are free.',
		h1: 'Animated flip clock for the new tab page',
		lead:
			'Here’s a flip clock for Chrome’s new tab, and then some. The split-flap cards are still here, next to eight other ways for a digit to change, from neon that flickers on to a paper sheet that tears off and falls. In the extension’s menu it’s now called Animated clock.',
		screenshot: {
			file: 'flip-clock-hero.webp',
			alt:
				'Nine animated clock designs in a three by three grid, all reading 10:24:35, over a snowy dusk',
			caption:
				'Nine designs; double-click a clock to flip to the next one',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Nine ways for a digit to change',
				p: [
					'Flip cards is the original, and any flip clock you set up before 4.0 stays on it. New clocks from the menu start in Glass, frosted cards where each digit turns over in 3D.',
					'After that it gets theatrical. Neon flickers each new digit on. Nixie tubes glow like old gas-discharge lamps. Odometer rolls its drums upwards. Aurora drops the card and uses thin digits with a shifting gradient. Paper tears off the old sheet and lets it fall — every second, if you leave seconds on. LED matrix switches a 5×7 grid of dots in a wave, and Retro LCD is a seven-segment display with a blinking colon.',
					'Searching for an aesthetic flip clock? Glass and Paper are the ones to try first, and Aurora if you’d rather have no cards at all.',
				],
			},
			{
				h2: 'Double-click for the next design',
				p: [
					'You don’t need a settings window to browse them. Double-click the clock and it moves on to the next design, showing its name and “double-click for the next one” underneath for a moment. Went one too far? Ctrl+Z.',
					'Each design keeps its own colours and font. Make the neon pink, wander through the other eight, come back, and it’s still pink.',
				],
			},
			{
				h2: 'Settings it never had before',
				p: [
					'Up to 3.6 the flip clock had no settings at all, just 24-hour time with seconds. Now there’s a Clock settings window with Time format (24-hour or 12-hour AM/PM), Show seconds, Layout and Timezone.',
					'Seconds are on by default, so something changes every second. Turn them off and the clock moves once a minute, which is calmer on a monitor parked on the new tab. Vertical stacks the digits into a narrow column for the edge of the page.',
					'Time zones cost nothing here, with the same 99 cities (daylight saving included) and fixed UTC offsets as the other clocks. The AM/PM label follows the interface language, so a Japanese interface gets 午前 and 午後.',
				],
				image: {
					file: 'flip-clock-vertical.webp',
					alt:
						'A vertical nixie tube clock showing 10:42 AM as a narrow column at the left edge, next to a date and a task list',
					caption: 'Vertical layout fits a side column',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'What it won’t do',
				p: [
					'Two formats, not twelve. German 18:35 Uhr and the other regional styles belong to the text clock. LED matrix and Retro LCD ignore the font picker, because their digits are made of dots and segments. Apart from Flip cards, the design sets the digit size, so there is no font-size box, border or corner radius to fiddle with.',
					'It’s light to run. One timer per clock, set for the next second, and a redraw only when a digit changes; the old version checked the time every 50 milliseconds. It needs no internet, and with reduced motion switched on in your system the digit animations stop.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Flip cards, Glass, Neon, Nixie tubes, Odometer, Aurora, Paper, LED matrix, Retro LCD. Double-click the clock to cycle through them.',
			},
			{
				name: 'Time format',
				detail:
					'24-hour or 12-hour (AM/PM), both previewed on the current time.',
			},
			{
				name: 'Show seconds',
				detail: 'On by default. Off means one change a minute.',
			},
			{
				name: 'Layout',
				detail:
					'Horizontal or Vertical, in settings or from the toolbox.',
			},
			{
				name: 'Timezone',
				detail:
					'Your computer, a city with daylight saving, or a fixed UTC offset. Free.',
			},
			{
				name: 'Colours and font',
				detail:
					'Digit colour, the colour behind the digits and a font, remembered separately for each design. LED matrix and Retro LCD skip the font.',
			},
			{
				name: 'Size',
				detail:
					'Proportions are locked, and each design has its own base size. New clocks arrive at 0.6 of it.',
			},
		],
		useCases: [
			'A desk clock on a monitor left on the new tab.',
			'Retro and synthwave themes built around Nixie tubes or Retro LCD.',
			'A narrow vertical clock down the side of a busy page.',
			'A second clock set to another city, without paying for it.',
		],
		faq: [
			{
				q: 'Is there an aesthetic flip clock for my browser?',
				a:
					'Yes, New Tab Studio’s animated clock puts a flip clock on the Chrome new tab, plus eight other designs. For an aesthetic look start with Glass or Paper, try Aurora if you’d rather skip the cards, and go for Nixie tubes or Retro LCD on a retro page. Double-click the clock to step through all nine.',
			},
			{
				q: 'Do the animated clock designs cost anything?',
				a:
					'No, all nine animated clock designs are free, and so are the 12-hour format, seconds, the vertical layout and the time zone. The clock does use one of your three free slots for widgets added from the menu. A clock that arrived as part of an installed theme leaves the slot open.',
			},
			{
				q: 'Can a flip clock show another city’s time?',
				a:
					'Yes, the animated clock can show any of 99 cities, and the time zone is free on it. Pick a city in Timezone and the clock follows its daylight saving changes, or choose a fixed UTC offset that never moves. Three of them side by side make a small world clock without PRO.',
			},
			{
				q: 'Where’s the flip clock in the menu?',
				a:
					'In New Tab Studio the flip clock sits under Time as Animated clock. New ones open in Glass, so choose Flip cards from the Design button or double-click your way round to it. Went one too far? Ctrl+Z undoes the switch.',
			},
			{
				q: 'Does the animation slow Chrome down?',
				a:
					'The animated clock shouldn’t slow Chrome down, since it redraws only when a digit changes, once a second at most. There’s one timer per clock and no network traffic. Turn Show seconds off and it changes once a minute, and with reduced motion on in your system the digit animations stop.',
			},
		],
		related: ['clock', 'analog-clock', 'wallpaper', 'text'],
		keywords: [
			'flip clock chrome extension',
			'aesthetic flip clock',
			'aesthetic clock',
			'animated clock new tab',
			'flip clock new tab',
		],
	},

	{
		slug: 'date',
		name: 'Date',
		category: 'Time',
		plan: 'free',
		model: 'DateModel',
		title: 'Date Widget for Chrome New Tab',
		metaDescription:
			'A date widget that tears off a calendar page at midnight. Six designs, week numbers, year progress, and proper month names in all ten interface languages.',
		h1: 'Date widget with a tear-off calendar',
		lead:
			'The date widget used to be one line of text. Now a new one arrives as a tear-off calendar, and at midnight yesterday’s sheet rips away. If you’ve ever opened a calendar just to check what day it is, this saves the trip.',
		screenshot: {
			file: 'date-hero.webp',
			alt:
				'Six date widget designs on a sunset photo, including a tear-off page and a glass card with week number and year progress',
			caption: 'Same Wednesday, six ways to write it',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A sheet that tears off at midnight',
				p: [
					'New date widgets start as a tear-off calendar page. At midnight the old page peels off over the new one in under a second. Leave the tab open overnight and it waits for you. Switch back in the morning and you’ll see yesterday go.',
					'The date also updates by itself now. The old widget kept showing yesterday until you reloaded the page, which is an odd thing for a date to do.',
				],
				image: {
					file: 'date-tear-off.webp',
					alt:
						'A tear-off calendar page for Wednesday the 14th lifting away at midnight to reveal Thursday',
					caption: 'At midnight yesterday’s page tears off by itself',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Six designs and a few extras',
				p: [
					'Classic is the line of text the widget always was, and your existing date widgets stay in it. Then there’s Tear-off calendar, Glass, Big weekday (the day name large, the date under it), Minimal, which is one thin line, and Compact, a pill with a small calendar leaf.',
					'What you can switch depends on the design. Day of the week and Year are on by default. Week number is off and follows ISO 8601, so weeks start on Monday. Year progress lives on Glass and shows “Day 273 of 365” with a percentage — motivating or alarming, depending on the month.',
				],
			},
			{
				h2: 'Formats and languages',
				p: [
					'Classic keeps its five formats: “12 Sep”, “12 September”, “12 Sep, Sat”, “12 September, Sat” and “12 Sat”. There is no numeric 12/09 option, and the other designs lay the date out their own way.',
					'The words come from the browser’s own date formatting in the interface language, so German, Japanese or Russian get real month names and the right word order (26 сентября, 9月26日) rather than an English template. All ten interface languages work, Classic included.',
				],
			},
			{
				h2: 'A date and time widget in two pieces',
				p: [
					'The usual pairing is a clock on top and the date underneath. Two widgets instead of one date and time widget, which means each gets its own size and font.',
					'Or skip the clock. Your taskbar shows the time already. It rarely shows the weekday in letters big enough to notice.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Tear-off calendar, Glass, Big weekday, Minimal, Compact. New widgets open as the tear-off page.',
			},
			{
				name: 'Date format',
				detail:
					'Five text formats, from “12 Sep” to “12 September, Sat”. Classic only.',
			},
			{
				name: 'Day of the week and Year',
				detail: 'Both on by default. Hide either for a shorter line.',
			},
			{
				name: 'Week number',
				detail: 'Off by default. ISO 8601, Monday first.',
			},
			{
				name: 'Year progress',
				detail: 'Glass only. “Day N of 365” and a percentage.',
			},
			{
				name: 'Toolbox',
				detail:
					'Depends on the design. The tear-off page has no border or text-style panel, since it’s an object rather than a box. Big weekday and Minimal get alignment and a shadow.',
			},
			{
				name: 'Opening settings',
				detail:
					'Double-click the date. It used to cycle formats; now it opens Date settings.',
			},
		],
		useCases: [
			'Under a big clock in the usual header block.',
			'A tear-off page in the corner of a cosy, desk-like theme.',
			'Week numbers for anyone whose job runs on “week 41”.',
			'Year progress on Glass, for a gentle nudge in the autumn.',
		],
		faq: [
			{
				q: 'Can I see the week number on my new tab?',
				a:
					'Yes, the date widget has a Week number switch in Date settings, off by default, which counts weeks the ISO 8601 way with Monday first. The switches on offer depend on the design, and Glass shows the week number beside year progress, “Day 273 of 365”. Google Calendar can show week numbers too, under its view options.',
			},
			{
				q:
					'How do I show the date and day of the week on Chrome’s new tab?',
				a:
					'Chrome’s standard new tab page doesn’t show the date, so you need a new tab extension for it. In New Tab Studio, add the Date widget and pick one of six designs. Big weekday puts the day name in large letters with the date under it, and the default tear-off page renews itself at midnight.',
			},
			{
				q: 'Does the date change at midnight by itself?',
				a:
					'Yes, the date widget updates itself at midnight, and the tear-off calendar design peels yesterday’s page away in under a second. Leave the tab open overnight and you’ll see the page go when you switch back in the morning. Before version 4.0 the widget kept showing yesterday until you reloaded.',
			},
			{
				q: 'Will the date show in my language?',
				a:
					'Yes, the date widget writes month and weekday names properly in all ten interface languages, with the right cases and word order, so Russian gets 26 сентября and Japanese gets 9月26日. Classic works in every one of them too. It always shows your computer’s date, though, rather than another time zone.',
			},
			{
				q: 'Is any part of the date widget paid?',
				a:
					'No, the date widget has no paid parts, so every design and every switch, year progress included, is free. It does take a slot, since the free plan covers three widgets you add yourself. A date that came with an installed theme leaves your three slots alone.',
			},
		],
		related: ['clock', 'calendar', 'day-countdown', 'text'],
		keywords: [
			'date widget',
			'date widget new tab',
			'tear off calendar widget',
			'date and time widget',
			'chrome new tab day of week',
		],
	},

	{
		slug: 'calendar',
		name: 'Calendar & Planner',
		category: 'Time',
		plan: 'free',
		model: 'CalendarModel',
		title: 'Calendar Widget for Chrome New Tab',
		metaDescription:
			'A calendar widget for the Chrome new tab. Month, week or a two-week list, your own events, and your Google Calendar brought in through its secret iCal link.',
		h1: 'Calendar widget with your Google Calendar in it',
		lead:
			'A calendar widget you never have to open, because it’s already on the new tab. Keep your own events in it, bring in your Google Calendar through its iCal link, and glance at the month every time you switch context.',
		screenshot: {
			file: 'calendar-hero.webp',
			alt:
				'A glass month calendar for October 2026 with colored events, a three-day conference bar and an Upcoming list',
			caption:
				'October at a glance, with the next two weeks listed on the right',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Month, Week or List',
				p: [
					'Three views, picked in Calendar settings. Month is the full grid. Week is one row of that grid, days rather than hours. List runs from today through the next 14 days, grouped by day, and when it’s empty it tells you the next two weeks are free. Nice to read.',
					'Make the widget wide and an Upcoming list appears beside the month. Make it short and the month becomes a strip of one or two weeks. It doesn’t zoom, it rearranges, so a small calendar stays readable.',
					'The header has the month, Today, arrows, a + for new events and the settings gear. There is no view switcher up there.',
				],
			},
			{
				h2: 'Adding and moving events',
				p: [
					'Click a day, or drag across several, and a New event form opens with an all-day event ready. The + button starts a meeting at the next full hour instead, or at 9:00 if today isn’t on screen.',
					'The form is short. Title, All day, Starts and Ends, one of ten colours, an optional description. Move the start and the end follows, keeping the length.',
					'Dragging moves an event by whole days and keeps its time. Only all-day and multi-day events stretch at the edge. With the widget focused, arrows move by day, PageUp and PageDown flip pages, T jumps to today and Enter makes a new event.',
				],
				image: {
					file: 'calendar-event-form.webp',
					alt:
						'A New event form with a title, start and end time, a purple color selected and a short description',
					caption: 'A real event card: time, color and notes',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Google Calendar on your desktop background, near enough',
				p: [
					'It isn’t literally your desktop, it’s the new tab, which you probably look at more. In Google Calendar’s settings, copy the Secret address in iCal format (the widget links straight to the right page), paste it into the Google Calendar section of the widget and press Connect. Chrome will ask to let the extension reach calendar.google.com. There’s no Google sign-in.',
					'It’s a subscription to that feed, not a live link. The widget refreshes when you open a new tab and more than 30 minutes have passed, or when you press Sync now. Google updates the secret address a few hours late on its side, so a meeting added a minute ago won’t turn up straight away.',
					'Imported events are read-only and get their own colour. Click one for the time, place, description and an Open in Google Calendar button. Repeating events are filled in from six months back to eighteen months ahead.',
					'The link stays in this browser. Save the page as a theme file or share it in the gallery and the link gets cut out, so a shared layout never carries your diary with it.',
				],
				image: {
					file: 'calendar-google-import.webp',
					alt:
						'An event card for Halloween imported from a public Google Calendar, marked read only, over the month view',
					caption:
						'Holidays from a public Google Calendar, read-only on your tab',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Four looks and one limit',
				p: [
					'Classic, Light, Dark and Glass. An existing calendar keeps Classic. A new one comes in Glass, or in Light or Dark when the page behind it is a solid colour. Accent color sets the highlight, and Week starts on settles the Monday-versus-Sunday argument.',
					'Your own events are free until you have more than fifteen; past that, adding one asks for PRO. Imported Google events do not count. Your events show on every calendar widget you add, while each widget keeps its own Google import, so a personal calendar and a work one in its own colour can sit side by side.',
					'Own events live in the browser, not in the theme. Anyone who installs your page gets the layout with an empty calendar.',
				],
			},
		],
		settings: [
			{
				name: 'View',
				detail: 'Month, Week or List. Chosen here, not in the header.',
			},
			{
				name: 'Upcoming events next to the month',
				detail:
					'On by default. Appears once the widget is wide enough.',
			},
			{
				name: 'Week starts on',
				detail: 'Auto (by language), Monday or Sunday.',
			},
			{
				name: 'Design and Accent color',
				detail:
					'Classic, Light, Dark or Glass, with eight accent options. Background and text colours are yours to set only in Classic.',
			},
			{
				name: 'Google Calendar',
				detail:
					'Secret iCal address, Connect, Sync now, Update link, Disconnect, and a colour for imported events.',
			},
			{
				name: 'Events',
				detail:
					'Title, all-day or timed, ten colours, a description. Stored in this browser.',
			},
		],
		useCases: [
			'Week planning next to a to-do list.',
			'Your work Google Calendar, read-only, on every new tab.',
			'Two calendars side by side, personal events and a work feed in its own colour.',
			'A two-week List in a narrow column where a month grid wouldn’t fit.',
		],
		faq: [
			{
				q: 'How do I put Google Calendar on my Chrome new tab page?',
				a:
					'Chrome has no setting that turns the new tab page into Google Calendar, so you either pin a calendar tab or use an extension. With New Tab Studio’s calendar widget, copy the Secret address in iCal format from Google Calendar’s settings, paste it into the widget’s Google Calendar section and press Connect. Chrome asks to let the extension reach calendar.google.com, and there’s no Google sign-in.',
			},
			{
				q:
					'Does the calendar widget sync both ways with Google Calendar?',
				a:
					'No, Google Calendar events arrive in the widget read-only through the iCal link, and nothing you add in the widget goes back to Google. Click an imported event to see its time, place and description, with an Open in Google Calendar button for changes. Each calendar widget can hold its own Google import in its own colour.',
			},
			{
				q: 'How often does the calendar update Google events?',
				a:
					'The calendar widget fetches your Google events when you open a new tab and more than 30 minutes have passed since the last sync, and Sync now forces it any time. Google refreshes the secret iCal address a few hours late on its side. A meeting added a minute ago may take a while to appear.',
			},
			{
				q: 'How many calendar events can I add for free?',
				a:
					'Your own events in the calendar widget are free until you have more than fifteen, and after that adding another asks for PRO. Events imported from Google Calendar never count toward that. The widget itself, all four looks and the Month, Week and List views cost nothing.',
			},
			{
				q: 'Where are my calendar events stored?',
				a:
					'Your own calendar events are stored in this browser, not inside the theme. Anyone who installs a page you published gets the layout with an empty calendar, and your secret iCal link is cut out when you export or share a theme. Your events show on every calendar widget you add.',
			},
		],
		related: ['google-calendar', 'todo-list', 'date', 'timetable'],
		keywords: [
			'calendar widget',
			'google calendar on desktop background',
			'how to put google calendar on desktop',
			'new tab calendar',
			'google calendar new tab',
		],
	},

	{
		slug: 'day-countdown',
		name: 'Day Countdown',
		category: 'Time',
		plan: 'free',
		model: 'CountDownModel',
		title: 'Day Countdown Widget for Chrome New Tab',
		metaDescription:
			'Days to go on a little calendar page that turns over as the number drops. A day countdown for the Chrome new tab, to any date you like or one of 23 holidays.',
		h1: 'Day countdown: days to go, on a calendar page',
		lead:
			'A day countdown puts one thing up front, the number of days, and adds the time as a clock. The main countdown lets you juggle units. This one doesn’t bother, because the days are what most people read anyway.',
		screenshot: {
			file: 'day-countdown-hero.webp',
			alt:
				'A calendar page showing 78 days to New Year’s Day beside a ring counting 12 days to a wedding',
			caption: 'New Year’s Day on a calendar page, the wedding on a ring',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Why a second countdown exists',
				p: [
					'Hand people eight unit switches and they turn most of them on. Then the widget is six numbers wide and they read one of them each morning. This version makes the call for you: days first, then the time as a clock.',
					'The one thing you can switch is Show seconds, in the new designs. The rest is about how it looks.',
				],
			},
			{
				h2: 'The calendar page design',
				p: [
					'New day countdowns open in Calendar page, a design no other widget has. It’s a little tear-off sheet, with the target month and year across the top, the number of days huge in the middle over “days to go”, then the time and the title. When the number drops, the page turns over.',
					'Ring is worth a look too. One big ring fills as the period runs out, with the days and time inside. Cards, Bold, Minimal and Poster come from the main countdown, and Classic is the old block, which is where your existing day countdowns stay.',
				],
			},
			{
				image: {
					file: 'day-countdown-layouts.webp',
					alt:
						'The same New Year’s Day countdown twice: a wide horizontal calendar page and a tall vertical one',
					caption:
						'One countdown, two layouts: wide for a top bar, tall for a side column',
					width: 1440,
					height: 900,
					small: true,
				},
				h2: 'Upright or on its side',
				p: [
					'Horizontal is a strip that tucks under a heading without pushing the page down. Vertical is a block that anchors a column next to a list. Switch it in settings or with the toolbox button.',
					'Pick the one that fits the gap you have, not the one that looks nicer on its own. And if you hide the target date, the widget no longer leaves an empty band where the date used to be.',
				],
			},
			{
				h2: 'Holidays and weekdays',
				p: [
					'Choose Holiday instead of a date and pick from the same 23 the main countdown has: Christmas, Diwali, Easter, Ramadan, Chinese New Year and the rest. It always aims at the next one, so a Christmas countdown set in December is still right the following December.',
					'Weekdays only drops Saturdays and Sundays. Perfect for a hand-in date, grim for anything fun. Nobody wants to know how many working days are left before their flight.',
				],
			},
		],
		settings: [
			{
				name: 'Count down to',
				detail:
					'Custom date, or Holiday with the title filled in for you.',
			},
			{
				name: 'Design',
				detail:
					'Classic, Cards, Calendar page, Bold, Ring, Minimal, Poster. Calendar page is the default for new ones.',
			},
			{
				name: 'Show seconds',
				detail:
					'New designs only, on by default. The single unit switch this widget has.',
			},
			{
				name: 'Show target date',
				detail:
					'Under the digits in Classic, as a caption or on the page in the others.',
			},
			{
				name: 'Weekdays only',
				detail: 'Skips weekends. Public holidays still count as days.',
			},
			{ name: 'Layout', detail: 'Horizontal strip or vertical block.' },
			{
				name: 'Title and Accent color',
				detail:
					'A label above the count and one of eight accents, both in the new designs.',
			},
		],
		useCases: [
			'A Christmas countdown on a calendar page, set once.',
			'A wedding date inside a Ring.',
			'A hand-in date in weekdays, above a task list.',
			'A vertical block down the side of a dashboard page.',
		],
		faq: [
			{
				q: 'Can I see how many days are left on my screen?',
				a:
					'Yes, a day countdown on the Chrome new tab shows the days left every time you open a tab. New Tab Studio’s version opens as a small calendar page with the number huge over “days to go”, and the page turns over each time the number drops. Count to any date or to one of 23 holidays.',
			},
			{
				q: 'Is there a Christmas countdown widget?',
				a:
					'Yes, the day countdown has Christmas among its 23 holidays, next to Diwali, Easter, Ramadan and Chinese New Year. Choose Holiday rather than a date, pick Christmas, and the title fills itself in. It always aims at the next one, so a countdown set this December is still right the December after.',
			},
			{
				q:
					'How is the day countdown different from the main countdown?',
				a:
					'The day countdown shows days plus a clock and has the Calendar page design, while the main countdown lets you choose units and repeat a date. The only unit switch here is Show seconds. A holiday still moves on to next year by itself, which covers most of what people want from repeats.',
			},
			{
				q: 'What happens when the day comes?',
				a:
					'When a day countdown reaches its date, Classic sits at zeros and the new designs switch to “Countdown is over!”. If the tab is open at that moment, confetti flies for a couple of seconds. It won’t beep or pop up a notification, so set a phone reminder for anything you mustn’t miss.',
			},
			{
				q: 'Does the day countdown need PRO?',
				a:
					'No, nothing in the day countdown sits behind PRO, including holidays, Weekdays only and all seven designs. Like anything you add from the menu, it takes up one of the three free widget places. One that came inside an installed theme doesn’t.',
			},
		],
		related: ['countdown', 'workday-countdown', 'count-up', 'date'],
		keywords: [
			'days until countdown extension',
			'day countdown chrome',
			'christmas countdown chrome extension',
			'holiday countdown new tab',
		],
	},

	{
		slug: 'workday-countdown',
		name: 'Workday Countdown',
		category: 'Time',
		plan: 'mixed',
		model: 'WorkdayCountdownModel',
		title: 'Workday Countdown Timer for Chrome New Tab',
		metaDescription:
			'How much of the working day is left, shown as a ring that fills from 9:30 to 18:30. A workday countdown timer for Chrome that resets itself at midnight.',
		h1: 'Workday countdown timer for the new tab',
		lead:
			'A workday countdown timer doesn’t aim at a date. It aims at the end of today, then at the end of tomorrow. It’s never finished, and it earns its keep at about four in the afternoon.',
		screenshot: {
			file: 'workday-countdown-hero.webp',
			alt:
				'A ring in the top right corner showing 4 hours 6 minutes of the workday left, with a task list in the middle',
			caption: 'Four hours to go, and three tasks still open',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A ring for the working day',
				p: [
					'New workday countdowns start in Ring, a circle that fills as the day goes, with “Workday” and the hours and minutes left in the middle and the seconds small beside them. Switch on Show working hours and it adds “9:30 AM – 6:30 PM” underneath.',
					'Minimal says the same with a thin bar and one line of text. Cards, Bold and Poster are there too, and Classic is the old plain countdown, which is what existing widgets keep.',
				],
			},
			{
				h2: 'Planning, not a pep talk',
				p: [
					'The obvious reading is motivational. The useful one is planning. At 15:40 with eighty minutes left you know not to start the two-hour job, and you’ve got time for the two small ones you’ve been dodging since Tuesday.',
					'Working from home, it turns into a stopping signal. No commute, no office emptying out around you — a number running out does that job instead.',
				],
			},
			{
				h2: 'Your hours, PRO and night shifts',
				p: [
					'Out of the box the day runs from 9:30 to 18:30. Adding the widget is free, despite the PRO badge on its tile in the menu. Setting your own finishing time is PRO, as it was before 4.0.',
					'The new designs add a second field, Workday starts at. It only feeds the ring or bar and the hours caption; the countdown itself cares about one thing, when you stop.',
					'Night shifts work too. Start at 22:00, finish at 6:00, and the ring runs through midnight from the evening before. That needs a custom finishing time, so it’s a PRO setup.',
				],
			},
			{
				h2: 'What it doesn’t know',
				p: [
					'Weekends, for a start. It counts to the finishing time every day, Saturday and Sunday included, and there is no weekdays-only switch here. Park it on a workspace you only open for work if the weekend count annoys you.',
					'After hours it reads “Workday is over!”, and the new designs throw confetti if the tab is open when the time runs out. After midnight it starts counting to today’s finish again, so at 1 a.m. it’ll cheerfully report seventeen and a half hours to go.',
					'There is no sound and no notification, so it never interrupts. You look at it when you want to know.',
				],
			},
		],
		settings: [
			{
				name: 'Workday ends at',
				detail:
					'The time the countdown aims for. 18:30 on the free plan; set your own with PRO.',
			},
			{
				name: 'Workday starts at',
				detail:
					'New designs only. Drives the ring or progress bar and the hours caption, not the countdown.',
			},
			{
				name: 'Units',
				detail:
					'Hours, minutes and seconds, plus milliseconds in Classic. All three are on at first; hours and minutes are usually plenty.',
			},
			{
				name: 'Show working hours',
				detail:
					'Adds the day’s span, like “9:30 AM – 6:30 PM”, under the count.',
			},
			{
				name: 'Design',
				detail:
					'Classic, Cards, Bold, Ring, Minimal, Poster. New widgets get Ring.',
			},
			{
				name: 'Layout',
				detail: 'Row or column, from settings or the toolbox.',
			},
			{
				name: 'Look',
				detail:
					'Font and colours from the toolbox. There’s no font-size box, so stretch the frame, and rounding sits in the Border panel.',
			},
		],
		useCases: [
			'A stopping signal when you work from home.',
			'Deciding at half three whether to start something big.',
			'Next to a task list, saying how much day is left for it.',
			'On a work-only workspace, so it’s out of sight in the evening.',
		],
		faq: [
			{
				q: 'How can I see how much of my workday is left?',
				a:
					'A workday countdown on your Chrome new tab counts the hours and minutes left until you finish, inside a ring that fills as the day goes. In New Tab Studio the day runs from 9:30 to 18:30 out of the box. At four in the afternoon one glance tells you whether the big job still fits.',
			},
			{
				q: 'Can I set my own working hours?',
				a:
					'Yes, with PRO you can move the finishing time, night shifts included, such as 22:00 to 6:00 with the ring running through midnight. Without PRO the workday countdown ends at 18:30 (6:30 PM), which is plenty to see whether it suits you.',
			},
			{
				q: 'Does the workday countdown skip weekends?',
				a:
					'No, the workday countdown runs to the finishing time every day of the week, Saturday and Sunday included, and has no weekdays-only switch. If that bothers you, put it on a workspace you only open for work. The weekdays-only option belongs to the date countdowns.',
			},
			{
				q: 'What happens when the workday ends?',
				a:
					'When the workday countdown runs out it reads “Workday is over!”, and the new designs throw confetti if the tab is open right then. There’s no sound and no notification. After midnight it starts counting to today’s finish again, so at 1 a.m. it shows seventeen and a half hours to go.',
			},
			{
				q: 'Is the workday countdown free despite the PRO badge?',
				a:
					'Yes, adding the workday countdown costs nothing even though its tile in the menu carries a PRO badge, and the six designs come free with it. PRO comes in only when you want to change the hours. The widget does count toward the three you can add yourself on the free plan.',
			},
		],
		related: ['countdown', 'day-countdown', 'todo-list', 'pomodoro'],
		keywords: [
			'workday countdown',
			'workday countdown timer',
			'end of work day countdown chrome',
			'time left in work day extension',
		],
	},
];
